<template>
  <modal class="veriff-modal" @close-modal="close">
    <div v-if="stage === 1" class="veriff-stage-one">
      <div class="veriff-stage-one__icon">
        <font-awesome-icon :icon="faShieldHalved" />
      </div>
      <h1 class="veriff-stage-one__title">Verification Required</h1>
      <p class="veriff-stage-one__subtitle">
        To deposit or withdraw funds, please complete a quick KYC verification.
        It usually takes less than 2 minutes.
      </p>

      <ul class="veriff-stage-one__benefits">
        <li class="veriff-benefit">
          <font-awesome-icon :icon="faClock" class="veriff-benefit__icon veriff-benefit__icon--time" />
          <span>Takes under 2 minutes</span>
        </li>
        <li class="veriff-benefit">
          <font-awesome-icon :icon="faIdCard" class="veriff-benefit__icon veriff-benefit__icon--id" />
          <span>Just a photo ID and a selfie</span>
        </li>
        <li class="veriff-benefit">
          <font-awesome-icon :icon="faLock" class="veriff-benefit__icon veriff-benefit__icon--secure" />
          <span>Encrypted &amp; securely processed</span>
        </li>
      </ul>

      <button class="veriff-cta" @click="startVeriff">
        <font-awesome-icon :icon="faShieldHalved" class="mr-2" />
        Continue
      </button>

      <p class="veriff-stage-one__footnote">
        <font-awesome-icon :icon="faLock" class="mr-1" />
        Powered by Veriff — your information stays private.
      </p>
    </div>

    <div v-if="stage === 2" class="veriff-stage-two">
      <div v-if="!veriffMounted" class="veriff-loading">
        <span class="veriff-spinner"></span>
        <p class="veriff-loading__text">Loading secure verification…</p>
      </div>
      <div id="veriff-root"></div>
    </div>
  </modal>
</template>

<script setup>
import { ref, onBeforeUnmount } from 'vue';
import Modal from '@/components/global/Modal.vue';
import { emitter } from '@/core/emitter.js';
import { PostHogUtils } from '@/core/PostHogUtils';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons/faShieldHalved';
import { faLock } from '@fortawesome/free-solid-svg-icons/faLock';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faIdCard } from '@fortawesome/free-solid-svg-icons/faIdCard';
const VERIFF_SDK_SCRIPT_ID = 'veriff-sdk-script';
const VERIFF_IN_CONTEXT_SCRIPT_ID = 'veriff-in-context-script';
const VERIFF_CANCELED_EVENT = 'CANCELED';
const stage = ref(1);
// Toggles the loading spinner off once the Veriff widget has mounted into
// #veriff-root, so users aren't staring at a blank panel while scripts load.
const veriffMounted = ref(false);
// One funnel outcome per modal open. The Veriff frame reports its own
// finished/canceled; every other way out (X button, unmount) must land as a
// kyc_canceled or the mid-funnel leak is invisible — kyc_required with no
// started/finished/canceled ever following it.
let outcomeCaptured = false;

// Loads a third-party script once and reuses it on subsequent calls.
const loadScript = (id, src) =>
  new Promise((resolve, reject) => {
    const existingScript = document.getElementById(id);
    if (existingScript) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });

// Initializes the Veriff flow: switches UI stage, loads SDK scripts, and mounts the verification widget.
const startVeriff = async () => {
  // User clicked "Continue" — intent to actually go through KYC, before the
  // third-party widget loads. This is the funnel step between hitting the gate
  // (kyc_required) and submitting docs.
  PostHogUtils.capture('kyc_started');
  stage.value = 2;
  veriffMounted.value = false;
  await loadScript(
    VERIFF_SDK_SCRIPT_ID,
    'https://cdn.veriff.me/sdk/js/1.5/veriff.min.js',
  );
  await loadScript(
    VERIFF_IN_CONTEXT_SCRIPT_ID,
    'https://cdn.veriff.me/incontext/js/v1/veriff.js',
  );

  if (typeof window.Veriff !== 'function' || !window.veriffSDK) {
    return;
  }
  const veriff = window.Veriff({
    host: 'https://stationapi.veriff.com',
    apiKey: import.meta.env.VITE_VERIFF_API_KEY,
    parentId: 'veriff-root',
    onSession: (_err, response) => {
      const verificationURL = response?.verification?.url;
      if (!verificationURL) return;
      window.veriffSDK.createVeriffFrame({
        url: verificationURL,
        onEvent: (message) => {
          const isCanceled =
            message === VERIFF_CANCELED_EVENT ||
            message?.type === VERIFF_CANCELED_EVENT ||
            message?.payload === VERIFF_CANCELED_EVENT;
          const isFinished = message === 'FINISHED';
          if (isCanceled || isFinished) {
            // Distinguish bailing out of the Veriff flow (the leak) from
            // actually submitting documents. The final approve/decline outcome
            // is captured separately as kyc_result once the backend responds.
            // stage='widget' = canceled from inside the Veriff frame (historic
            // kyc_canceled rows without a stage are all this path). Honors
            // outcomeCaptured: a dismissal/unmount may have settled this open
            // already, and a trailing frame event must not add a second one.
            if (!outcomeCaptured) {
              outcomeCaptured = true;
              PostHogUtils.capture(
                isFinished ? 'kyc_finished' : 'kyc_canceled',
                isFinished ? {} : { stage: 'widget' },
              );
            }
            close(response?.verification?.id);
          }
        },
      });
    },
  });
  veriff.setParams({
    vendorData: ' ',
  });
  veriff.mount();
  veriffMounted.value = true;
};

// Leaving the flow without a Veriff-frame outcome (X button on either stage,
// or the modal unmounting under the user) is a cancel: record where the user
// bailed. stage='intro' = never clicked Continue (no kyc_started precedes
// it); stage='widget' = left after entering the Veriff flow.
function captureDismissal() {
  if (outcomeCaptured) return;
  outcomeCaptured = true;
  PostHogUtils.capture('kyc_canceled', {
    stage: stage.value === 2 ? 'widget' : 'intro',
  });
}

// Notifies the app that the modal should close and passes the Veriff session id when available.
async function close(sessionID) {
  captureDismissal();
  emitter.emit('showVeriffModal', sessionID);
}

// Torn down without close() (logout, layout teardown) — settle the outcome.
onBeforeUnmount(captureDismissal);
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/theme/variables.scss' as theme;

/* ---- Stage 1: intro / call-to-action ---- */
.veriff-stage-one {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0.5rem 0.25rem;
}

.veriff-stage-one__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 18px;
  border-radius: 50%;
  font-size: 26px;
  color: #ffffff;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  box-shadow: 0 6px 18px rgba(99, 102, 241, 0.35);
}

.veriff-stage-one__title {
  margin: 0 0 10px;
  color: var(--color-primary_01);
  font-size: 26px;
  line-height: 1.2;
  font-weight: 700;
}

.veriff-stage-one__subtitle {
  max-width: 360px;
  margin: 0 0 22px;
  color: var(--grey-2200);
  font-size: 15px;
  line-height: 1.55;
}

.veriff-stage-one__benefits {
  width: 100%;
  max-width: 360px;
  margin: 0 0 24px;
  padding: 16px 18px;
  list-style: none;
  text-align: left;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.12) 100%);
  border: 1px solid rgba(150, 156, 203, 0.2);

  [theme='night'] & {
    background: linear-gradient(135deg, rgba(150, 156, 203, 0.1) 0%, rgba(134, 144, 219, 0.15) 100%);
    border-color: rgba(150, 156, 203, 0.25);
  }
}

.veriff-benefit {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 7px 0;
  color: var(--color-primary_01);
  font-size: 14px;
  font-weight: 500;

  &:first-child { padding-top: 0; }
  &:last-child { padding-bottom: 0; }
}

.veriff-benefit__icon {
  width: 18px;
  font-size: 16px;
  flex-shrink: 0;

  &--time { color: #6366f1; }
  &--id { color: #8b5cf6; }
  &--secure { color: #10b981; }
}

.veriff-cta {
  width: 100%;
  max-width: 360px;
  border: none;
  border-radius: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #ffffff;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
  }
}

.veriff-stage-one__footnote {
  margin: 16px 0 0;
  color: var(--grey-2200);
  font-size: 12px;
  line-height: 1.4;
}

/* ---- Stage 2: hosted Veriff widget ---- */
.veriff-stage-two {
  min-height: 120px;
}

.veriff-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 36px 0;
}

.veriff-spinner {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(99, 102, 241, 0.2);
  border-top-color: #6366f1;
  animation: veriff-spin 0.8s linear infinite;
}

.veriff-loading__text {
  margin: 0;
  color: var(--grey-2200);
  font-size: 14px;
}

@keyframes veriff-spin {
  to { transform: rotate(360deg); }
}

/* ---- Hosted Veriff SDK form overrides ---- */
:deep(#veriff-root .veriff-container .veriff-description) {
  margin-top: 12px;
  color: var(--grey-2200);
  font-size: 14px;
  line-height: 1.4;
}

:deep(#veriff-root .veriff-container .veriff-text) {
  background-color: var(--white_01);
  border: var(--border-02);
  color: var(--blue-201);

  &::placeholder {
    color: var(--blue-201);
    opacity: 1;
  }

  &:focus {
    box-shadow: none;
  }
}

:deep(#veriff-root .veriff-container .veriff-label) {
  color: var(--blue-201);
}

:deep(#veriff-root .veriff-container .veriff-submit) {
  border: none;
  border-radius: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
  }
}
</style>
